import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, BookmarkPlus } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";

const QUOTE_TYPES = ["Quote", "Invoice", "Scope of Work", "Change Order"];
const PRIMARY_TYPES = ["Quote", "Invoice"];
const SECONDARY_TYPES = ["Scope of Work", "Change Order"];
const TRADES = ["General Renovation", "Masonry", "Electrical", "Plumbing", "Painting", "Roofing", "Flooring", "Exterior Work", "Apartment Turnover", "Other"];
const TONES = ["Simple", "Professional", "Detailed", "Premium", "Short and direct"];

export default function NewQuotePage() {
  const navigate = useNavigate();
  const { subscription } = useBilling();
  const hasTemplates = subscription?.status === "active" && ["pro", "crew"].includes(subscription?.plan);
  const [templates, setTemplates] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [profile, setProfile] = useState({ company_name: "", contractor_name: "", phone: "", email: "", address: "" });
  const [form, setForm] = useState({
    quote_type: "Quote",
    trade_category: "General Renovation",
    customer_name: "", customer_phone: "", customer_email: "", job_address: "",
    project_title: "",
    rough_notes: "",
    total_price: "",
    itemized: true,
    num_items: 3,
    special_notes: "",
    exclusions: "",
    tone: "Professional",
  });

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (hasTemplates) {
      api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    }
  }, [hasTemplates]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyTemplate = (templateId) => {
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    setForm((f) => ({
      ...f,
      quote_type: t.quote_type || f.quote_type,
      trade_category: t.trade_category || f.trade_category,
      tone: t.tone || f.tone,
      num_items: t.num_items || f.num_items,
      itemized: t.itemized,
      rough_notes: t.rough_notes_template || f.rough_notes,
      special_notes: t.special_notes || f.special_notes,
      exclusions: t.exclusions || f.exclusions,
    }));
    toast.success(`Loaded "${t.name}"`);
  };

  const onGenerate = async (e) => {
    e.preventDefault();
    if (!form.rough_notes.trim()) {
      toast.error("Add some rough notes first.");
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        quote_type: form.quote_type,
        trade_category: form.trade_category,
        rough_notes: form.rough_notes,
        total_price: form.total_price ? Number(form.total_price) : null,
        itemized: form.itemized,
        num_items: Number(form.num_items || 3),
        special_notes: form.special_notes,
        exclusions: form.exclusions,
        tone: form.tone,
        project_title: form.project_title,
      };
      const gen = await api.post("/quotes/generate", payload);
      const saved = await api.post("/quotes", {
        quote_type: form.quote_type,
        trade_category: form.trade_category,
        company: profile,
        customer: {
          name: form.customer_name,
          phone: form.customer_phone,
          email: form.customer_email,
          address: form.job_address,
        },
        rough_notes: form.rough_notes,
        generated: gen.data,
        total_price: form.total_price ? Number(form.total_price) : (gen.data.total || 0),
        status: "Draft",
      });
      toast.success("Quote generated.");
      navigate(`/app/quotes/${saved.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="new-quote-page">
      <h1 className="font-display text-3xl font-bold text-slate-900">New Quote</h1>
      <p className="text-slate-500 mt-1 mb-8">Paste your rough notes. ScopeWriter will turn them into a professional quote.</p>

      {hasTemplates && templates.length > 0 && (
        <Card className="p-4 border-orange-200 bg-orange-50/40 mb-6 flex items-center gap-3" data-testid="template-loader">
          <BookmarkPlus className="h-4 w-4 text-orange-600 shrink-0" />
          <div className="text-sm text-slate-700 shrink-0">Start from a template:</div>
          <Select onValueChange={applyTemplate}>
            <SelectTrigger className="bg-white" data-testid="template-select"><SelectValue placeholder="Choose a saved template…" /></SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name} · {t.trade_category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Card>
      )}

      <form onSubmit={onGenerate} className="space-y-6">
        {/* Document type — prominent pill toggle */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">What are you sending?</h2>
          <div className="flex flex-wrap items-center gap-2" data-testid="doc-type-toggle">
            {PRIMARY_TYPES.map((t) => {
              const active = form.quote_type === t;
              return (
                <button
                  type="button"
                  key={t}
                  onClick={() => update("quote_type", t)}
                  data-testid={`doc-type-${t.toLowerCase()}`}
                  className={`px-5 h-11 rounded-full text-sm font-semibold transition-colors border ${
                    active
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                  }`}
                >
                  {t}
                </button>
              );
            })}
            <div className="ml-2 pl-2 border-l border-slate-200 flex flex-wrap gap-2">
              {SECONDARY_TYPES.map((t) => {
                const active = form.quote_type === t;
                return (
                  <button
                    type="button"
                    key={t}
                    onClick={() => update("quote_type", t)}
                    data-testid={`doc-type-${t.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-3 h-9 rounded-full text-xs font-medium transition-colors border ${
                      active
                        ? "bg-orange-50 border-orange-300 text-orange-700"
                        : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Trade */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Trade</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Trade category</Label>
              <Select value={form.trade_category} onValueChange={(v) => update("trade_category", v)}>
                <SelectTrigger data-testid="select-trade"><SelectValue /></SelectTrigger>
                <SelectContent>{TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Customer */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Customer info</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Customer name" testid="customer-name">
              <Input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="customer-name-input" />
            </Field>
            <Field label="Customer phone" testid="customer-phone">
              <Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} data-testid="customer-phone-input" />
            </Field>
            <Field label="Customer email" testid="customer-email">
              <Input type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="customer-email-input" />
            </Field>
            <Field label="Job address" testid="job-address">
              <Input value={form.job_address} onChange={(e) => update("job_address", e.target.value)} data-testid="job-address-input" />
            </Field>
          </div>
        </Card>

        {/* Job details */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Job details</h2>
          <div className="space-y-4">
            <Field label="Project title (optional)">
              <Input value={form.project_title} onChange={(e) => update("project_title", e.target.value)} placeholder="e.g. Front step rebuild" data-testid="project-title-input" />
            </Field>
            <Field label="Rough job notes" required>
              <Textarea
                value={form.rough_notes}
                onChange={(e) => update("rough_notes", e.target.value)}
                placeholder="demo old steps, rebuild 4x4 platform, beige brick, limestone treads, 4490"
                className="min-h-[140px]"
                data-testid="rough-notes-input"
                required
              />
            </Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Total price ($)">
                <Input type="number" step="0.01" value={form.total_price} onChange={(e) => update("total_price", e.target.value)} placeholder="4490" data-testid="total-price-input" />
              </Field>
              <Field label="Tone">
                <Select value={form.tone} onValueChange={(v) => update("tone", v)}>
                  <SelectTrigger data-testid="select-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="# line items">
                <Input type="number" min="1" max="15" value={form.num_items} onChange={(e) => update("num_items", e.target.value)} disabled={!form.itemized} data-testid="num-items-input" />
              </Field>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div>
                <div className="text-sm font-medium text-slate-800">Itemized breakdown</div>
                <div className="text-xs text-slate-500">Split the total into clear line items.</div>
              </div>
              <Switch checked={form.itemized} onCheckedChange={(v) => update("itemized", v)} data-testid="itemized-switch" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Special notes (optional)">
                <Textarea value={form.special_notes} onChange={(e) => update("special_notes", e.target.value)} className="min-h-[80px]" data-testid="special-notes-input" />
              </Field>
              <Field label="Exclusions (optional)">
                <Textarea value={form.exclusions} onChange={(e) => update("exclusions", e.target.value)} className="min-h-[80px]" data-testid="exclusions-input" />
              </Field>
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/app")} data-testid="cancel-button">Cancel</Button>
          <Button
            type="submit" disabled={generating} data-testid="generate-quote-button"
            className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-6"
          >
            {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate Professional Quote</>}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-orange-500 ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}
